import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'
import { getProductosTienda, getMisSuscripciones, getKitsConProductos, cambiarKitSuscripcion } from '../../lib/portalClienteApi'
import CheckoutFlow from '../../components/CheckoutFlow'
import ClienteLayout from '../../components/ClienteLayout'
import TrustBadge from '../../components/TrustBadge'

const CATEGORIAS = [
  { id: 'todos',      label: 'Todo',         icono: '🛍️' },
  { id: 'multiuso',   label: 'Multiuso',     icono: '🌟' },
  { id: 'lavarropa',  label: 'Ropa',         icono: '🧺' },
  { id: 'lavandinas', label: 'Lavandinas',   icono: '🧪' },
  { id: 'cocina',     label: 'Cocina',       icono: '🧴' },
  { id: 'bano',       label: 'Baño',         icono: '🛁' },
  { id: 'papel',      label: 'Papel',        icono: '🧻' },
  { id: 'higiene',    label: 'Higiene',      icono: '🧼' },
  { id: 'bolsas',     label: 'Bolsas',       icono: '🛍️' },
  { id: 'accesorios', label: 'Accesorios',   icono: '🧽' },
]

const TIER_COLOR = {
  A: { bg: '#e0f7fa', color: '#006064', label: 'Premium' },
  B: { bg: '#fff3e0', color: '#e65100', label: 'Calidad/precio' },
  C: { bg: '#e8f5e9', color: '#2e7d32', label: 'Marca masiva' },
  D: { bg: '#f1f5f9', color: '#475569', label: 'Especialidades' },
}

const STORAGE_CARRITO = 'limpack_carrito_v1'

export default function Tienda() {
  const { user, profile } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const embedded = location.pathname.startsWith('/cliente/tienda')
  const [searchParams, setSearchParams] = useSearchParams()

  const [productos, setProductos] = useState([])
  const [productoDetalle, setProductoDetalle] = useState(null)  // null | { producto } — para modal
  const [kits, setKits] = useState([])
  const [cargando, setCargando] = useState(true)
  const [categoria, setCategoria] = useState('todos')
  const [busqueda, setBusqueda] = useState('')
  const [mostrarCarrito, setMostrarCarrito] = useState(false)
  const [mostrarCheckout, setMostrarCheckout] = useState(false)
  const [checkoutKit, setCheckoutKit] = useState(null)  // si elige pedir un kit
  const [pedidoCreado, setPedidoCreado] = useState(null)
  const [esSuscriptor, setEsSuscriptor] = useState(false)
  const [direccionSuscriptor, setDireccionSuscriptor] = useState(null)
  const [suscActiva, setSuscActiva] = useState(null)  // datos completos de la suscripción activa
  const [kitExpandido, setKitExpandido] = useState(null)
  const [tierKitActivo, setTierKitActivo] = useState('essential')

  // Carrito persistente en localStorage
  const [carrito, setCarrito] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_CARRITO)) || [] } catch { return [] }
  })
  useEffect(() => {
    localStorage.setItem(STORAGE_CARRITO, JSON.stringify(carrito))
  }, [carrito])

  useEffect(() => {
    setCargando(true)
    Promise.all([
      getProductosTienda(),
      getKitsConProductos(),
      user?.id ? getMisSuscripciones(user.id) : Promise.resolve([]),
    ]).then(([prods, ks, suscs]) => {
      setProductos(prods)
      setKits(ks)
      const activa = suscs.find(s => s.estado === 'activa')
      setEsSuscriptor(!!activa)
      setSuscActiva(activa || null)
      // Tomamos la dirección de la suscripción activa (donde el user es titular o beneficiario)
      if (activa) {
        setDireccionSuscriptor({
          barrioId: activa.barrio_obj?.id || null,
          barrioNombre: activa.barrio_obj?.nombre || activa.barrio || '',
          direccion: activa.direccion || '',
          apto: activa.apto || '',
          tipoVivienda: activa.tipo_vivienda || '',
          lat: activa.lat,
          lng: activa.lng,
        })
      }
      setCargando(false)
    })
  }, [user?.id])

  // Deep-link: si vienen con ?sku=XXX (ej: desde el tip diario), abrir modal de detalle
  useEffect(() => {
    const sku = searchParams.get('sku')
    if (!sku || productos.length === 0) return
    const prod = productos.find(p => p.sku === sku)
    if (prod) {
      setProductoDetalle(prod)
      // Limpiamos la URL para que si cierra y vuelve a abrir no re-aparezca
      const newParams = new URLSearchParams(searchParams)
      newParams.delete('sku')
      setSearchParams(newParams, { replace: true })
    }
  }, [searchParams, productos])

  const productosFiltrados = useMemo(() => {
    let f = productos
    if (categoria !== 'todos') f = f.filter(p => p.categoria === categoria)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase()
      f = f.filter(p =>
        p.nombre.toLowerCase().includes(q) ||
        (p.marca || '').toLowerCase().includes(q) ||
        p.sku.toLowerCase().includes(q)
      )
    }
    return f
  }, [productos, categoria, busqueda])

  // Categorías con conteo
  const categoriasCount = useMemo(() => {
    const c = {}
    productos.forEach(p => { c[p.categoria] = (c[p.categoria] || 0) + 1 })
    c.todos = productos.length
    return c
  }, [productos])

  function agregar(producto) {
    setCarrito(prev => {
      const ix = prev.findIndex(it => it.sku === producto.sku)
      if (ix >= 0) {
        const cp = [...prev]
        cp[ix] = { ...cp[ix], cantidad: cp[ix].cantidad + 1 }
        return cp
      }
      return [...prev, {
        sku: producto.sku,
        nombre: producto.nombre,
        marca: producto.marca,
        presentacion: producto.presentacion,
        precio_unit: Number(producto.precio_publico),
        cantidad: 1,
      }]
    })
  }

  function cambiarCantidad(sku, delta) {
    setCarrito(prev => prev
      .map(it => it.sku === sku ? { ...it, cantidad: Math.max(0, it.cantidad + delta) } : it)
      .filter(it => it.cantidad > 0)
    )
  }

  function quitar(sku) {
    setCarrito(prev => prev.filter(it => it.sku !== sku))
  }

  function vaciarCarrito() {
    if (!confirm('¿Vaciar el carrito?')) return
    setCarrito([])
  }

  const subtotal = carrito.reduce((s, it) => s + it.precio_unit * it.cantidad, 0)
  const cantidadTotal = carrito.reduce((s, it) => s + it.cantidad, 0)

  async function logout() {
    await supabase.auth.signOut()
    navigate('/login', { replace: true })
  }

  /* ──────── Pedido completado ──────── */
  if (pedidoCreado) {
    setTimeout(() => {
      try { localStorage.removeItem(STORAGE_CARRITO) } catch {}
    }, 100)
    return (
      <div className="lp-app">
        <Topbar user={user} onLogout={logout} carritoCount={0} onCarritoClick={() => {}} />
        <main className="t-success">
          <div className="t-success-card">
            <div className="t-success-icon">✓</div>
            <h1>¡Pedido creado!</h1>
            <p>Número de pedido: <strong>{pedidoCreado.numero_orden}</strong></p>
            <p className="t-success-msg">
              Te enviamos los datos para transferir por WhatsApp. Apenas confirmemos el pago,
              tu pedido entra a la cola para entregarse en la fecha que elegiste.
            </p>
            <div className="t-success-actions">
              <Link to="/cliente" className="t-btn-primary">Ir a Mi cuenta</Link>
              <Link to="/cliente/tienda" reloadDocument className="t-btn-ghost">Seguir comprando</Link>
            </div>
          </div>
        </main>
        <style>{styles}</style>
      </div>
    )
  }

  /* ──────── Checkout ──────── */
  if (mostrarCheckout) {
    // 2 modos: comprar kit (one-shot) o checkout productos sueltos
    const esKit = !!checkoutKit
    return (
      <div className="lp-app">
        <Topbar user={user} onLogout={logout} carritoCount={cantidadTotal} onCarritoClick={() => { setMostrarCheckout(false); setCheckoutKit(null) }} />
        <CheckoutFlow
          tipo={esKit ? 'compra_unica' : 'productos_sueltos'}
          kitCodigo={esKit ? checkoutKit.codigo : null}
          subtotalKit={esKit ? Number(checkoutKit.precio_unica) : 0}
          itemsIniciales={esKit ? [] : carrito}
          esSuscriptor={esSuscriptor}
          direccionSuscriptor={direccionSuscriptor}
          tituloHero={esKit ? `Pedir ${checkoutKit.nombre}` : 'La Tienda Limpack'}
          onCancelar={() => { setMostrarCheckout(false); setCheckoutKit(null) }}
          onCompletado={(p) => setPedidoCreado(p)}
        />
        <style>{styles}</style>
      </div>
    )
  }

  /* ──────── Catálogo principal ──────── */
  const tiendaContent = (
    <div className={`lp-app${embedded ? ' lp-embedded' : ''}`}>
      {!embedded && (
        <Topbar user={user} onLogout={logout} carritoCount={cantidadTotal} onCarritoClick={() => setMostrarCarrito(true)} />
      )}

      {embedded ? (
        <header className="t-hero-mini">
          <div className="t-hero-mini-info">
            <div className="t-hero-mini-pill">{productos.length} productos · sin envío extra dentro del día del barrio</div>
            <h1 className="t-hero-mini-h1">La Tienda Lim<span className="t-brand">pack</span></h1>
            <p className="t-hero-mini-sub">Comprá productos sueltos. Los recibís el día de la semana que vos quieras.</p>
          </div>
          <button type="button" className="t-hero-mini-cart" onClick={() => setMostrarCarrito(true)} aria-label="Carrito">
            🛍️ {cantidadTotal > 0 && <span className="t-hero-mini-badge">{cantidadTotal}</span>}
          </button>
        </header>
      ) : (
        <section className="t-hero">
          <div className="t-hero-pill">
            <span className="t-hero-dot"></span>
            La Tienda Limpack · {productos.length} productos
          </div>
          <h1 className="t-hero-h1">
            Pedí lo que <span>necesitás</span>
          </h1>
          <p className="t-hero-sub">
            {esSuscriptor
              ? 'Sumás cualquier producto a tu próxima entrega. Envío gratis cuando pasamos por tu cuadra.'
              : 'Sin permanencia, sin suscripción. Pedí los productos que querés y te los llevamos cuando pasamos por tu barrio (o cuando te quede mejor).'}
          </p>
          <button
            type="button"
            className="t-hero-scroll"
            onClick={() => {
              const target = document.querySelector('.t-toolbar') || document.querySelector('.t-grid')
              target?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            aria-label="Ver productos"
          >
            Ver productos
            <span className="t-hero-scroll-arrow">↓</span>
          </button>
        </section>
      )}

      <main className="t-main">
        {/* SECCIÓN KITS — disponible siempre */}
        {kits.length > 0 && (
          <section className="t-kits-section" id="kits-section">
            <div className="t-kits-header">
              <div>
                <h2>📦 {esSuscriptor ? 'Cambiar mi kit o agregar otra suscripción' : 'Pedí un kit por única vez'}</h2>
                <p>{esSuscriptor ? 'Cambiá el kit que tenés activo o sumá una segunda dirección/casa.' : 'Sin atadura. Probá un kit completo y recibilo en tu próxima entrega.'}</p>
              </div>
              <div className="t-kits-toggle">
                <button className={tierKitActivo === 'essential' ? 'on' : ''} onClick={() => setTierKitActivo('essential')}>Essential</button>
                <button className={tierKitActivo === 'premium' ? 'on' : ''} onClick={() => setTierKitActivo('premium')}>Premium</button>
                <button className={tierKitActivo === 'comercial' ? 'on' : ''} onClick={() => setTierKitActivo('comercial')}>Comercial</button>
              </div>
            </div>
            <div className="t-kits-grid">
              {kits.filter(k => k.tier === tierKitActivo).map(k => {
                const expandido = kitExpandido === k.codigo
                const esMiKit = esSuscriptor && suscActiva?.kit_actual === k.codigo
                return (
                  <div key={k.codigo} className={`t-kit${esMiKit ? ' t-kit-actual' : ''}`}>
                    {esMiKit && <div className="t-kit-badge t-kit-badge-actual">✓ Tu kit actual</div>}
                    {!esMiKit && k.destacado && <div className="t-kit-badge">⭐ Más popular</div>}
                    <div className="t-kit-head">
                      <h3>{k.nombre}</h3>
                      <span className="t-kit-meta">{k.personas} · {k.productos.length} productos</span>
                    </div>
                    {k.descripcion && <p className="t-kit-desc">{k.descripcion}</p>}
                    {k.precio_susc && Number(k.precio_susc) < Number(k.precio_unica) ? (() => {
                      const ahorro = Number(k.precio_unica) - Number(k.precio_susc)
                      const ahorroPct = Math.round((ahorro / Number(k.precio_unica)) * 100)
                      return (
                        <>
                          {/* HERO: precio de suscripción */}
                          <div className="t-kit-precio t-kit-precio-susc">
                            <div className="t-kit-precio-monto">
                              <strong>${Number(k.precio_susc).toLocaleString('es-UY')}</strong>
                              <span className="t-kit-precio-mes">/mes</span>
                            </div>
                            <div className="t-kit-precio-tag">Suscripción · IVA incluido</div>
                            <div className="t-kit-ahorro-chip">−{ahorroPct}% vs compra única</div>
                          </div>
                          {/* Secundario: compra única */}
                          <div className="t-kit-precio-alt">
                            O una vez: <strong>${Number(k.precio_unica).toLocaleString('es-UY')}</strong> <span>IVA incl.</span>
                          </div>
                        </>
                      )
                    })() : (
                      <div className="t-kit-precio">
                        <strong>${Number(k.precio_unica).toLocaleString('es-UY')}</strong>
                        <span>compra única · IVA incl.</span>
                      </div>
                    )}
                    <button className="t-kit-ver" onClick={() => setKitExpandido(expandido ? null : k.codigo)}>
                      {expandido ? 'Ocultar productos ▴' : 'Ver qué incluye ▾'}
                    </button>
                    {expandido && (
                      <>
                      <ul className="t-kit-productos">
                        {k.productos.map((p, i) => (
                          <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{p.cantidad}×</span>
                            <strong>{p.nombre}</strong>
                            {p.presentacion && <em>· {p.presentacion}</em>}
                            {p.marca && <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b', fontWeight: 600 }}>{p.marca}</span>}
                          </li>
                        ))}
                      </ul>
                      <div style={{
                        marginTop: 8, padding: '8px 10px',
                        background: '#fffbeb', border: '1px solid #fcd34d',
                        borderRadius: 6, fontSize: 10, color: '#78350f', lineHeight: 1.4,
                      }}>
                        ⚠️ Marcas sujetas a stock. Si falta alguna, la reemplazamos por una <strong>alternativa de igual calidad y precio</strong>, testeada por nosotros.
                      </div>
                      </>
                    )}
                    {esMiKit ? (
                      <Link to="/cliente/mi-plan" className="t-kit-comprar t-kit-comprar-actual">
                        Ver mi plan →
                      </Link>
                    ) : esSuscriptor ? (
                      <div className="t-kit-actions">
                        <button className="t-kit-comprar" onClick={async () => {
                          if (!confirm(`¿Cambiar tu suscripción al ${k.nombre}? El próximo cobro será de $${Number(k.precio_susc || k.precio_unica).toLocaleString('es-UY')}/mes.`)) return
                          try {
                            await cambiarKitSuscripcion(suscActiva.id, k.codigo)
                            alert('¡Listo! Cambiamos tu kit. Las próximas entregas vendrán con los productos del nuevo kit.')
                            navigate('/cliente/mi-plan')
                          } catch (e) {
                            alert('No se pudo cambiar el kit: ' + e.message)
                          }
                        }}>
                          Cambiar mi kit a este →
                        </button>
                        <Link to={`/cliente/nueva-suscripcion?kit=${k.codigo}`} className="t-kit-comprar-ghost">
                          + Agregar como nueva suscripción
                        </Link>
                      </div>
                    ) : (
                      <button className="t-kit-comprar" onClick={() => { setCheckoutKit(k); setMostrarCheckout(true) }}>
                        Pedir este kit →
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
            <div className="t-divider"><span>o pedí productos sueltos</span></div>
          </section>
        )}

        {/* Compromiso Limpack — solo modo público */}
        {!embedded && (<section className="t-promise">
          <div className="t-promise-head">
            <span className="t-promise-tag">💚 COMPROMISO LIMPACK</span>
            <h2>Por qué Limpack</h2>
          </div>
          <div className="t-promise-grid">
            <div className="t-promise-item">
              <div className="t-promise-icon">💰</div>
              <strong>10% abajo del súper</strong>
              <p>Siempre por debajo del precio de supermercado. El precio que ves es el que pagás.</p>
            </div>
            <div className="t-promise-item">
              <div className="t-promise-icon">🚚</div>
              <strong>Llega a tu puerta</strong>
              <p>No vas al súper, no cargás bolsas. Te lo dejamos en tu casa cuando pasamos por el barrio.</p>
            </div>
            <div className="t-promise-item">
              <div className="t-promise-icon">✅</div>
              <strong>Si no nos convence, no entra</strong>
              <p>Cada marca pasó por una casa de Limpack antes de llegar a la tuya.</p>
            </div>
            <div className="t-promise-item">
              <div className="t-promise-icon">🤝</div>
              <strong>Sin permanencia</strong>
              <p>Comprás cuando querés, cancelás cuando querés. Sin letra chica.</p>
            </div>
          </div>
        </section>)}

        {/* Upsell suscripción — banner hero para no-suscriptores (skip si embedded) */}
        {!embedded && !esSuscriptor && (
          <section className="t-upsell-hero">
            <div className="t-upsell-hero-bg"></div>
            <div className="t-upsell-hero-content">
              <span className="t-upsell-hero-tag">⭐ Ahorrá en serio</span>
              <h2 className="t-upsell-hero-h2">Nunca más falta el jabón de lavar la ropa.</h2>
              <p className="t-upsell-hero-sub">
                Ni el papel higiénico, ni la lavandina, ni el lavavajillas. Tu kit completo en la puerta cada mes — más barato que armarlo uno por uno.
              </p>
              <ul className="t-upsell-hero-list">
                <li><span>✓</span> <strong>Envío gratis</strong> los días que pasamos por tu barrio</li>
                <li><span>✓</span> <strong>Pausás o cambiás</strong> cuando querés. Sin permanencia.</li>
                <li><span>✓</span> <strong>Te devolvemos lo extra del mes 1</strong> repartido en los próximos dos cobros</li>
                <li><span>✓</span> <strong>Descuento Colaborativo:</strong> 5% extra si tu vecino también arma su kit</li>
              </ul>
              <div className="t-upsell-hero-cta-row">
                <button className="t-upsell-hero-cta" onClick={() => navigate('/cliente/nueva-suscripcion')}>
                  Armar mi kit · desde $1.150/mes →
                </button>
                <span className="t-upsell-hero-fineprint">Cancelás cuando querés</span>
              </div>
            </div>
          </section>
        )}

        {/* Buscador + categorías */}
        <div className="t-toolbar">
          <input
            type="search"
            placeholder="Buscar producto, marca, SKU…"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            className="t-search"
          />
          <div className="t-cats">
            {CATEGORIAS.map(c => (
              <button
                key={c.id}
                className={`t-cat ${categoria === c.id ? 'is-active' : ''}`}
                onClick={() => setCategoria(c.id)}
              >
                <span>{c.icono}</span>
                {c.label}
                <em>{categoriasCount[c.id] || 0}</em>
              </button>
            ))}
          </div>
        </div>

        {/* Grid productos */}
        {cargando && <div className="t-loading">Cargando catálogo…</div>}

        {!cargando && productosFiltrados.length === 0 && (
          <div className="t-empty">
            <div className="t-empty-icon">🔍</div>
            <h3>Sin resultados</h3>
            <p>Probá con otra búsqueda o categoría.</p>
          </div>
        )}

        <div className="t-grid">
          {productosFiltrados.map(p => {
            const enCarrito = carrito.find(it => it.sku === p.sku)
            const tier = TIER_COLOR[p.tier] || TIER_COLOR.D
            const tieneFicha = p.beneficios?.length > 0 || p.aroma || p.descripcion
            return (
              <div key={p.sku} className="t-prod">
                <button
                  type="button"
                  onClick={() => setProductoDetalle(p)}
                  className="t-prod-clickable"
                  aria-label={`Ver detalles de ${p.nombre}`}
                >
                  <div className="t-prod-img">
                    {p.imagen_url
                      ? <img src={p.imagen_url} alt={p.nombre} loading="lazy" />
                      : <div className="t-prod-img-placeholder">{CATEGORIAS.find(c => c.id === p.categoria)?.icono || '📦'}</div>}
                    <span className="t-prod-tier-badge" style={{ background: tier.bg, color: tier.color }}>{tier.label}</span>
                    {p.precio_mercado && p.ahorro_pct >= 5 && (
                      <span className="t-prod-ahorro">−{p.ahorro_pct}%</span>
                    )}
                  </div>
                  <div className="t-prod-info">
                    <div className="t-prod-marca">{p.marca || '—'}</div>
                    <div className="t-prod-nombre">{p.nombre}</div>
                    <div className="t-prod-presentacion">{p.presentacion}{tieneFicha && ' · + info'}</div>
                    <div className="t-prod-precios">
                      <strong className="t-prod-precio">${Number(p.precio_publico).toLocaleString('es-UY')}</strong>
                      {p.precio_mercado && p.ahorro_pct >= 5 && (
                        <del className="t-prod-mercado">${Number(p.precio_mercado).toLocaleString('es-UY')}</del>
                      )}
                    </div>
                    <div className="t-prod-iva">IVA incluido</div>
                  </div>
                </button>
                {enCarrito ? (
                  <div className="t-prod-cant">
                    <button onClick={() => cambiarCantidad(p.sku, -1)}>−</button>
                    <span>{enCarrito.cantidad}</span>
                    <button onClick={() => cambiarCantidad(p.sku, +1)}>+</button>
                  </div>
                ) : (
                  <button className="t-prod-add" onClick={() => agregar(p)}>+ Agregar</button>
                )}
              </div>
            )
          })}
        </div>

        {/* Modal detalle de producto */}
        {productoDetalle && (
          <ModalProductoDetalle
            producto={productoDetalle}
            enCarrito={carrito.find(it => it.sku === productoDetalle.sku)}
            onClose={() => setProductoDetalle(null)}
            onAgregar={() => { agregar(productoDetalle); }}
            onCambiarCantidad={(d) => cambiarCantidad(productoDetalle.sku, d)}
          />
        )}

        {/* Trust badge al final del catálogo */}
        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px solid #e2e8f0' }}>
          <TrustBadge variant="default" />
        </div>
      </main>

      {/* Carrito flotante */}
      {cantidadTotal > 0 && !mostrarCarrito && (
        <button className="t-cart-fab" onClick={() => setMostrarCarrito(true)}>
          🛒 Ver carrito ({cantidadTotal}) · ${subtotal.toLocaleString('es-UY')}
        </button>
      )}

      {/* Drawer carrito */}
      {mostrarCarrito && (
        <div className="t-drawer-bg" onClick={() => setMostrarCarrito(false)}>
          <div className="t-drawer" onClick={e => e.stopPropagation()}>
            <div className="t-drawer-head">
              <h2>Tu carrito ({cantidadTotal})</h2>
              <button onClick={() => setMostrarCarrito(false)} className="t-drawer-x">✕</button>
            </div>
            {carrito.length === 0 ? (
              <p className="t-drawer-empty">Todavía no agregaste nada.</p>
            ) : (
              <>
                <ul className="t-drawer-items">
                  {carrito.map(it => (
                    <li key={it.sku}>
                      <div className="t-drawer-info">
                        <strong>{it.nombre}</strong>
                        <span>{it.presentacion} {it.marca && `· ${it.marca}`}</span>
                      </div>
                      <div className="t-drawer-cant">
                        <button onClick={() => cambiarCantidad(it.sku, -1)}>−</button>
                        <span>{it.cantidad}</span>
                        <button onClick={() => cambiarCantidad(it.sku, +1)}>+</button>
                      </div>
                      <div className="t-drawer-precio">${(it.precio_unit * it.cantidad).toLocaleString('es-UY')}</div>
                      <button onClick={() => quitar(it.sku)} className="t-drawer-quitar" aria-label="Quitar">×</button>
                    </li>
                  ))}
                </ul>
                <div className="t-drawer-totales">
                  <div><span>Subtotal</span><strong>${subtotal.toLocaleString('es-UY')}</strong></div>
                  <p className="t-drawer-info-text">El envío se calcula al confirmar según tu barrio y día.</p>
                </div>
                <div className="t-drawer-actions">
                  <button className="t-btn-ghost" onClick={vaciarCarrito}>Vaciar</button>
                  <button className="t-btn-primary" onClick={() => { setMostrarCarrito(false); setMostrarCheckout(true) }}>
                    Ir al checkout →
                  </button>
                </div>
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'center' }}>
                  <TrustBadge variant="inline" />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <style>{styles}</style>
    </div>
  )

  return embedded ? <ClienteLayout title="La Tienda">{tiendaContent}</ClienteLayout> : tiendaContent
}

/* ─── Modal de detalle de producto ─── */
function ModalProductoDetalle({ producto, enCarrito, onClose, onAgregar, onCambiarCantidad }) {
  const tier = TIER_COLOR[producto.tier] || TIER_COLOR.D
  const beneficios = Array.isArray(producto.beneficios) ? producto.beneficios : []
  const precio = Number(producto.precio_publico)
  const mercado = producto.precio_mercado ? Number(producto.precio_mercado) : null
  const ahorro = mercado ? mercado - precio : 0

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  return (
    <div className="t-modal-bg" onClick={onClose}>
      <div className="t-modal" onClick={e => e.stopPropagation()}>
        <button className="t-modal-close" onClick={onClose} aria-label="Cerrar">×</button>

        <div className="t-modal-grid">
          {/* IZQ: Foto */}
          <div className="t-modal-foto">
            {producto.imagen_url
              ? <img src={producto.imagen_url} alt={producto.nombre} />
              : <div className="t-modal-foto-placeholder">{CATEGORIAS.find(c => c.id === producto.categoria)?.icono || '📦'}</div>}
          </div>

          {/* DER: Info */}
          <div className="t-modal-info">
            <div className="t-modal-marca">{producto.marca || '—'}</div>
            <h2 className="t-modal-nombre">{producto.nombre}</h2>

            <div className="t-modal-meta">
              <span className="t-modal-pres">{producto.presentacion}</span>
              {producto.aroma && <span className="t-modal-pill">🌸 {producto.aroma}</span>}
              <span className="t-modal-pill" style={{ background: tier.bg, color: tier.color }}>{tier.label}</span>
            </div>

            {producto.descripcion && (
              <p className="t-modal-desc">{producto.descripcion}</p>
            )}

            {/* Precio + comparación */}
            <div className="t-modal-precio-block">
              <div className="t-modal-precio-row">
                <div>
                  <div className="t-modal-precio">${precio.toLocaleString('es-UY')}</div>
                  <div className="t-modal-iva">IVA incluido</div>
                </div>
                {mercado && ahorro > 0 && (
                  <div className="t-modal-ahorro-block">
                    <div className="t-modal-mercado">Mercado <s>${mercado.toLocaleString('es-UY')}</s></div>
                    <div className="t-modal-ahorro">Ahorrás ${ahorro.toLocaleString('es-UY')}</div>
                  </div>
                )}
              </div>
            </div>

            {/* CTA */}
            {enCarrito ? (
              <div className="t-modal-cant">
                <button onClick={() => onCambiarCantidad(-1)}>−</button>
                <span>{enCarrito.cantidad} en carrito</span>
                <button onClick={() => onCambiarCantidad(+1)}>+</button>
              </div>
            ) : (
              <button className="t-modal-add" onClick={onAgregar}>
                + Agregar al carrito
              </button>
            )}

            {/* Beneficios */}
            {beneficios.length > 0 && (
              <div className="t-modal-section">
                <h3>Beneficios</h3>
                <ul className="t-modal-benef">
                  {beneficios.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              </div>
            )}

            {/* Detalles técnicos */}
            {(producto.rendimiento || producto.modo_uso || producto.ideal_para || producto.composicion) && (
              <div className="t-modal-section">
                <h3>Detalles</h3>
                <dl className="t-modal-dl">
                  {producto.rendimiento && (
                    <><dt>Rendimiento</dt><dd>{producto.rendimiento}</dd></>
                  )}
                  {producto.ideal_para && (
                    <><dt>Ideal para</dt><dd>{producto.ideal_para}</dd></>
                  )}
                  {producto.modo_uso && (
                    <><dt>Modo de uso</dt><dd>{producto.modo_uso}</dd></>
                  )}
                  {producto.composicion && (
                    <><dt>Composición</dt><dd>{producto.composicion}</dd></>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function Topbar({ onLogout, carritoCount, onCarritoClick, user }) {
  return (
    <nav className="lp-nav">
      <div className="lp-nav-inner">
        <Link to={user ? "/cliente" : "/"} className="lp-logo">
          <img src="/isotipo.png" alt="" className="lp-logo-iso" />
          <span>Lim<em>pack</em></span>
        </Link>
        <div className="lp-nav-actions">
          {carritoCount > 0 && (
            <button onClick={onCarritoClick} className="lp-nav-cart">
              🛒 <em>{carritoCount}</em>
            </button>
          )}
          {user ? (
            <>
              <Link to="/cliente" className="lp-nav-link">← Mi cuenta</Link>
              <button onClick={onLogout} className="lp-nav-logout">Salir</button>
            </>
          ) : (
            <>
              <Link to="/login" className="lp-nav-link">Iniciar sesión</Link>
              <Link to="/suscribirme" className="lp-nav-logout" style={{ textDecoration: 'none', display: 'inline-block' }}>Suscribirme</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  )
}

const styles = `
:root {
  --navy:  #1a3461;
  --blue:  #1a5cb8;
  --teal:  #00c4d4;
  --cream: #F8FAFC;
  --line2: #e2e8f0;
  --muted2:#64748b;
}
html, body { background: var(--cream); margin: 0 }
.lp-app { min-height: 100vh; background: var(--cream); font-family: 'Inter', system-ui, sans-serif; color: var(--navy); display: flex; flex-direction: column }
/* Modo embedded (dentro de /cliente/tienda con sidebar) */
.lp-app.lp-embedded { min-height: auto; background: var(--cl-bg, #f5f7fb); width: 100%; max-width: 100%; overflow-x: hidden }
.lp-app.lp-embedded .t-main { padding-top: 0 }

/* Mini-hero compacto para modo embedded */
.t-hero-mini {
  width: 100%; max-width: 1320px; margin: 0 auto;
  padding: 18px 16px 8px;
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  text-align: left; box-sizing: border-box;
}
.t-hero-mini-info { flex: 1; min-width: 0; text-align: left }
.t-hero-mini-pill {
  display: inline-block; padding: 4px 12px;
  background: var(--cl-teal, #00C4D4); color: #0F1F3D;
  border-radius: 999px; font-size: 10.5px; font-weight: 800;
  letter-spacing: .06em; text-transform: uppercase;
  margin-bottom: 10px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;
}
.t-hero-mini-h1 { font-size: 26px; font-weight: 900; color: var(--cl-navy, #0F1F3D); letter-spacing: -.02em; margin: 0; line-height: 1.1; text-align: left }
.t-hero-mini-h1 .t-brand { color: var(--cl-teal, #00C4D4) }
.t-hero-mini-sub { font-size: 13.5px; color: var(--cl-muted, #64748b); margin: 6px 0 0; line-height: 1.5; text-align: left; max-width: 540px }
.t-hero-mini-cart {
  position: relative; flex-shrink: 0;
  background: var(--cl-card, #fff); border: 1px solid var(--cl-line, #e2e8f0);
  border-radius: 50%; width: 44px; height: 44px;
  font-size: 20px; cursor: pointer; font-family: inherit;
  display: flex; align-items: center; justify-content: center;
  padding: 0;
}
.t-hero-mini-cart:hover { border-color: var(--cl-teal, #00C4D4) }
.t-hero-mini-badge {
  position: absolute; top: -4px; right: -4px;
  background: var(--cl-teal, #00C4D4); color: #0F1F3D;
  font-size: 10px; font-weight: 800; padding: 2px 6px;
  border-radius: 999px; min-width: 18px; text-align: center;
  border: 2px solid #fff;
}
@media (min-width: 900px) {
  .t-hero-mini { padding: 28px 44px 12px }
  .t-hero-mini-h1 { font-size: 34px }
  .t-hero-mini-sub { font-size: 14px }
  .t-hero-mini-cart { width: 50px; height: 50px; font-size: 22px }
}
.lp-embedded .t-main { max-width: 1320px; padding: 8px 16px 32px }
@media (min-width: 900px) { .lp-embedded .t-main { padding: 8px 44px 36px } }

/* Kit actual + acciones para suscriptores */
.t-kit-actual { border: 2px solid #16a34a !important; box-shadow: 0 8px 22px rgba(22,163,74,.15) }
.t-kit-badge-actual { background: #16a34a !important; color: #fff !important }
.t-kit-actions { display: flex; flex-direction: column; gap: 8px }
.t-kit-comprar-actual { background: #16a34a !important; text-decoration: none; display: block; text-align: center }
.t-kit-comprar-ghost {
  display: block; text-align: center; padding: 9px 14px;
  background: transparent; border: 1.5px solid var(--cl-line, #e2e8f0);
  border-radius: 999px; font-size: 12px; font-weight: 700;
  color: var(--cl-navy, #0F1F3D); text-decoration: none;
  transition: border-color .15s;
}
.t-kit-comprar-ghost:hover { border-color: var(--cl-teal, #00C4D4) }

/* Navbar */
.lp-nav { position: sticky; top: 0; z-index: 50; background: rgba(26,52,97,.94); backdrop-filter: blur(12px) }
.lp-nav-inner { max-width: 1200px; margin: 0 auto; padding: 0 16px; height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 12px }
.lp-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; color: #fff }
.lp-logo-iso { width: 32px; height: 32px; background: #fff; border-radius: 8px; padding: 3px; box-sizing: border-box; object-fit: contain }
.lp-logo span { font-size: 22px; font-weight: 900; letter-spacing: -.02em }
.lp-logo em { font-style: normal; color: var(--teal) }
.lp-nav-actions { display: flex; gap: 10px; align-items: center }
.lp-nav-link { color: rgba(255,255,255,.78); text-decoration: none; font-size: 13px; font-weight: 600; padding: 8px 12px; border-radius: 999px }
.lp-nav-link:hover { background: rgba(255,255,255,.1); color: var(--teal) }
.lp-nav-logout { background: rgba(255,255,255,.12); color: #fff; border: 0; padding: 8px 14px; border-radius: 999px; font-size: 13px; font-weight: 600; cursor: pointer; font-family: inherit }
.lp-nav-cart { background: var(--teal); color: var(--navy); border: 0; padding: 8px 14px; border-radius: 999px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit; display: inline-flex; align-items: center; gap: 6px }
.lp-nav-cart em { font-style: normal; background: var(--navy); color: #fff; border-radius: 999px; padding: 2px 8px; font-size: 11px }

/* Hero */
.t-hero { background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00c4d4 100%); color: #fff; padding: 48px 16px 56px; text-align: center }
.t-hero-pill { display: inline-flex; align-items: center; gap: 8px; background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.18); padding: 6px 14px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 14px }
.t-hero-dot { width: 7px; height: 7px; background: var(--teal); border-radius: 50%; animation: pulse 1.6s infinite }
@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
.t-hero-h1 { font-size: clamp(28px, 5vw, 44px); font-weight: 900; line-height: 1.1; letter-spacing: -.02em; margin: 0 0 12px }
.t-hero-h1 span { color: var(--teal) }
.t-hero-scroll {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 22px; padding: 12px 24px;
  background: rgba(255,255,255,.12); border: 1.5px solid rgba(255,255,255,.30);
  color: #fff; font-weight: 700; font-size: 14px;
  border-radius: 999px; cursor: pointer; font-family: inherit;
  transition: background .2s, transform .2s;
  backdrop-filter: blur(8px);
}
.t-hero-scroll:hover { background: rgba(255,255,255,.20); transform: translateY(-1px) }
.t-hero-scroll-arrow { font-size: 16px; animation: bounce-down 1.8s ease-in-out infinite }
@keyframes bounce-down {
  0%, 100% { transform: translateY(0) }
  50% { transform: translateY(4px) }
}
.t-hero-sub { font-size: clamp(14px, 2.4vw, 16px); color: rgba(255,255,255,.82); max-width: 600px; margin: 0 auto }

/* Main */
.t-main { flex: 1; max-width: 1200px; width: 100%; margin: 0 auto; padding: 24px 16px 100px; box-sizing: border-box }

/* Sección Kits */
.t-kits-section { margin-bottom: 32px }
.t-kits-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 16px; margin-bottom: 18px; flex-wrap: wrap }
.t-kits-header h2 { font-size: 22px; font-weight: 900; color: var(--navy); margin: 0; letter-spacing: -.01em }
.t-kits-header p { font-size: 14px; color: var(--muted2); margin: 4px 0 0 }
.t-kits-toggle { background: #fff; border: 1px solid var(--line2); border-radius: 999px; padding: 4px; display: inline-flex; box-shadow: 0 2px 8px rgba(26,52,97,.04) }
.t-kits-toggle button { padding: 8px 16px; border-radius: 999px; border: 0; background: transparent; font-size: 12px; font-weight: 800; letter-spacing: .04em; color: rgba(26,52,97,.55); cursor: pointer; font-family: inherit }
.t-kits-toggle button.on { background: var(--navy); color: #fff }
.t-kits-grid { display: grid; grid-template-columns: 1fr; gap: 14px; align-items: start }
@media (min-width: 700px) { .t-kits-grid { grid-template-columns: repeat(2, 1fr) } }
@media (min-width: 1100px) { .t-kits-grid { grid-template-columns: repeat(3, 1fr) } }
.t-kit { position: relative; background: #fff; border: 1.5px solid var(--line2); border-radius: 16px; padding: 20px; transition: transform .15s, box-shadow .15s, border-color .15s; display: flex; flex-direction: column; gap: 10px }
.t-kit:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(26,52,97,.1); border-color: var(--teal) }
.t-kit-badge { position: absolute; top: -10px; left: 16px; background: var(--teal); color: var(--navy); padding: 3px 10px; border-radius: 999px; font-size: 10px; font-weight: 800; letter-spacing: .03em }
.t-kit-head h3 { font-size: 17px; font-weight: 900; color: var(--navy); margin: 0; letter-spacing: -.01em }
.t-kit-meta { font-size: 11px; color: var(--muted2); font-weight: 700 }
.t-kit-desc { font-size: 13px; color: #475569; line-height: 1.45; margin: 0; min-height: 36px }
.t-kit-precio { display: flex; align-items: baseline; gap: 8px; padding: 10px 0; border-top: 1px dashed var(--line2); border-bottom: 1px dashed var(--line2) }
.t-kit-precio strong { font-size: 24px; font-weight: 900; color: var(--navy); letter-spacing: -.02em }
.t-kit-precio span { font-size: 11px; color: var(--muted2); font-weight: 600 }

/* Bloque hero suscripción */
.t-kit-precio-susc {
  display: block !important;
  background: #ecfdf5; border: 1px solid #86efac; border-radius: 12px;
  padding: 14px 16px !important; margin: 4px 0 8px;
  border-top: 1px solid #86efac !important; border-bottom: 1px solid #86efac !important;
  position: relative;
}
.t-kit-precio-susc .t-kit-precio-monto { display: flex; align-items: baseline; gap: 4px }
.t-kit-precio-susc .t-kit-precio-monto strong { font-size: 30px !important; color: #065f46 !important; font-weight: 900 }
.t-kit-precio-susc .t-kit-precio-mes { font-size: 14px !important; color: #065f46 !important; font-weight: 700 !important }
.t-kit-precio-susc .t-kit-precio-tag { font-size: 11px; color: #065f46; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; margin-top: 2px }
.t-kit-ahorro-chip {
  position: absolute; top: 10px; right: 12px;
  font-size: 10px; font-weight: 800; padding: 3px 8px;
  background: #15803d; color: #fff; border-radius: 999px;
  letter-spacing: .04em;
}

/* Compra única (secundario) */
.t-kit-precio-alt {
  font-size: 12px; color: var(--muted2); font-weight: 600;
  padding: 0 0 10px; border-bottom: 1px dashed var(--line2);
  margin-bottom: 4px;
}
.t-kit-precio-alt strong { color: var(--navy); font-size: 14px; font-weight: 800 }
.t-kit-precio-alt span { font-size: 10px; color: var(--muted2); font-weight: 600; margin-left: 4px; text-transform: uppercase; letter-spacing: .04em }
.t-kit-ver { background: transparent; border: 0; font-size: 12px; font-weight: 700; color: var(--teal); cursor: pointer; padding: 6px 0; text-align: left; font-family: inherit }
.t-kit-ver:hover { text-decoration: underline }
.t-kit-productos { list-style: none; padding: 12px 14px; margin: 0; background: #f8fafc; border-radius: 10px; font-size: 12px }
.t-kit-productos li { padding: 4px 0; border-bottom: 1px solid var(--line2); display: flex; gap: 4px; align-items: baseline; flex-wrap: wrap }
.t-kit-productos li:last-child { border-bottom: 0 }
.t-kit-productos span { font-weight: 800; color: var(--teal); min-width: 24px }
.t-kit-productos strong { color: var(--navy); font-weight: 700 }
.t-kit-productos em { font-style: normal; color: var(--muted2); font-size: 11px }
.t-kit-comprar { background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; padding: 12px 18px; font-weight: 800; font-size: 13px; cursor: pointer; font-family: inherit; box-shadow: 0 4px 14px rgba(0,196,212,.25); margin-top: auto }
.t-kit-comprar:hover { transform: translateY(-1px) }

.t-divider { display: flex; align-items: center; gap: 12px; color: var(--muted2); font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .08em; margin: 32px 0 16px }
.t-divider::before, .t-divider::after { content: ''; flex: 1; height: 1px; background: var(--line2) }

/* Toolbar */
.t-toolbar { display: flex; flex-direction: column; gap: 14px; margin-bottom: 24px; padding: 16px; background: #fff; border-radius: 16px; box-shadow: 0 4px 20px rgba(26,52,97,.06); border: 1px solid var(--line2); position: sticky; top: 64px; z-index: 40 }
.t-search { width: 100%; box-sizing: border-box; padding: 12px 16px; font-size: 14px; border: 1.5px solid var(--line2); border-radius: 999px; background: #f8fafc; font-family: inherit }
.t-search:focus { outline: none; border-color: var(--teal); box-shadow: 0 0 0 3px rgba(0,196,212,.15); background: #fff }
.t-cats { display: flex; gap: 6px; overflow-x: auto; -webkit-overflow-scrolling: touch; padding-bottom: 4px }
.t-cat { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; background: #f1f5f9; border: 1.5px solid transparent; border-radius: 999px; font-size: 13px; font-weight: 700; color: var(--navy); cursor: pointer; font-family: inherit; white-space: nowrap; transition: all .15s }
.t-cat:hover { background: #e2e8f0 }
.t-cat.is-active { background: var(--navy); color: #fff }
.t-cat em { font-style: normal; font-size: 11px; opacity: .7; font-weight: 700 }
.t-cat.is-active em { color: var(--teal) }

/* Grid */
.t-loading, .t-empty { text-align: center; padding: 60px 20px; color: var(--muted2) }
.t-empty-icon { font-size: 48px; margin-bottom: 14px }
.t-empty h3 { color: var(--navy); margin: 0 0 6px }
.t-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px }
@media (min-width: 700px) { .t-grid { grid-template-columns: repeat(3, 1fr); gap: 16px } }
@media (min-width: 1000px) { .t-grid { grid-template-columns: repeat(4, 1fr); gap: 20px } }
@media (min-width: 1400px) { .t-grid { grid-template-columns: repeat(5, 1fr) } }
.t-iva-banner { background: linear-gradient(90deg, #fef3c7 0%, #fde68a 100%); padding: 10px 16px; border-radius: 10px; font-size: 13px; text-align: center; color: #92400e; font-weight: 600; margin-bottom: 16px }

.t-prod { display: flex; flex-direction: column; background: #fff; border-radius: 16px; overflow: hidden; box-shadow: 0 1px 3px rgba(15,31,61,.06); transition: transform .25s cubic-bezier(.4,0,.2,1), box-shadow .25s cubic-bezier(.4,0,.2,1); position: relative }
.t-prod-clickable { display: flex; flex-direction: column; flex: 1; background: transparent; border: 0; padding: 0; text-align: left; cursor: pointer; font-family: inherit; color: inherit; width: 100% }
.t-prod-img { position: relative; aspect-ratio: 4/5; background: var(--line2, #f1f5f9); overflow: hidden; display: block }
.t-prod-img img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s cubic-bezier(.4,0,.2,1); display: block }
.t-prod:hover .t-prod-img img { transform: scale(1.04) }
.t-prod-img-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 64px; opacity: .4 }
.t-prod-tier-badge { position: absolute; top: 10px; left: 10px; padding: 3px 9px; border-radius: 999px; font-size: 9px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; backdrop-filter: blur(8px); background: rgba(255,255,255,.95); color: var(--navy) }
.t-prod-ahorro { position: absolute; top: 10px; right: 10px; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; background: var(--teal, #00C4D4); color: #fff }

/* MODAL detalle producto */
.t-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.62); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 16px; backdrop-filter: blur(4px); animation: fadein .2s ease }
@keyframes fadein { from { opacity: 0 } to { opacity: 1 } }
.t-modal { background: #fff; border-radius: 18px; max-width: 920px; width: 100%; max-height: 92vh; overflow-y: auto; position: relative; box-shadow: 0 30px 80px rgba(0,0,0,.25); animation: slideup .25s cubic-bezier(.16,1,.3,1) }
@keyframes slideup { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
.t-modal-close { position: absolute; top: 14px; right: 14px; width: 36px; height: 36px; border-radius: 50%; background: #f1f5f9; border: 0; font-size: 22px; color: var(--navy); cursor: pointer; z-index: 2; transition: background .15s; font-family: inherit; line-height: 1 }
.t-modal-close:hover { background: #e2e8f0 }
.t-modal-grid { display: grid; grid-template-columns: 420px 1fr; gap: 0 }
@media (max-width: 720px) { .t-modal-grid { grid-template-columns: 1fr } }
.t-modal-foto { background: linear-gradient(135deg, #f8fafc, #ede9e0); position: relative; min-height: 100%; overflow: hidden; border-radius: 18px 0 0 18px }
@media (max-width: 720px) { .t-modal-foto { border-radius: 18px 18px 0 0; aspect-ratio: 1/1; min-height: 0 } }
.t-modal-foto img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; display: block }
@media (max-width: 720px) { .t-modal-foto img { position: relative; inset: auto } }
.t-modal-foto-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 100px; line-height: 1; opacity: .4 }
.t-modal-info { padding: 32px 28px }
.t-modal-marca { font-size: 11px; font-weight: 800; color: var(--blue); letter-spacing: .12em; text-transform: uppercase; margin-bottom: 6px }
.t-modal-nombre { font-size: 24px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; margin: 0 0 14px; line-height: 1.2 }
.t-modal-meta { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 16px }
.t-modal-pres { background: #f1f5f9; color: var(--navy); padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 700 }
.t-modal-pill { padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase }
.t-modal-desc { color: #475569; font-size: 14px; line-height: 1.55; margin: 0 0 18px }
.t-modal-precio-block { background: linear-gradient(135deg, rgba(0,196,212,.06), rgba(26,92,184,.04)); border: 1px solid rgba(0,196,212,.18); border-radius: 12px; padding: 14px 16px; margin-bottom: 14px }
.t-modal-precio-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap }
.t-modal-precio { font-size: 32px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; line-height: 1 }
.t-modal-iva { font-size: 10px; color: var(--muted2); font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-top: 4px }
.t-modal-ahorro-block { text-align: right }
.t-modal-mercado { font-size: 11px; color: var(--muted2); font-weight: 600 }
.t-modal-ahorro { font-size: 13px; color: #16a34a; font-weight: 800; margin-top: 2px }
.t-modal-add { display: block; width: 100%; padding: 14px 20px; background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; font-size: 15px; font-weight: 800; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.25); transition: transform .12s, box-shadow .12s; margin-bottom: 18px }
.t-modal-add:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(0,196,212,.35) }
.t-modal-cant { display: flex; align-items: center; justify-content: space-between; gap: 8px; background: #f0fcfd; border: 1.5px solid var(--teal); padding: 8px 16px; border-radius: 999px; margin-bottom: 18px }
.t-modal-cant button { width: 36px; height: 36px; border-radius: 50%; border: 0; background: var(--navy); color: #fff; font-weight: 800; font-size: 18px; cursor: pointer; font-family: inherit }
.t-modal-cant button:hover { background: var(--teal); color: var(--navy) }
.t-modal-cant span { font-weight: 800; color: var(--navy); flex: 1; text-align: center }
.t-modal-section { margin-top: 18px; padding-top: 18px; border-top: 1px solid var(--line2) }
.t-modal-section h3 { font-size: 11px; font-weight: 800; color: var(--navy); letter-spacing: .08em; text-transform: uppercase; margin: 0 0 10px }
.t-modal-benef { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px }
.t-modal-benef li { padding-left: 20px; position: relative; color: var(--navy); font-size: 13px; line-height: 1.45 }
.t-modal-benef li:before { content: '✓'; position: absolute; left: 0; color: #16a34a; font-weight: 900 }
.t-modal-dl { display: grid; grid-template-columns: 110px 1fr; gap: 8px 14px; margin: 0; font-size: 13px }
.t-modal-dl dt { color: var(--muted2); font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: .04em }
.t-modal-dl dd { color: var(--navy); margin: 0; line-height: 1.5 }
.t-prod:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(15,31,61,.12) }
.t-prod-info { padding: 14px }
.t-prod-marca { font-size: 11px; font-weight: 800; color: var(--blue, #1a5cb8); text-transform: uppercase; letter-spacing: .06em; margin-bottom: 4px }
.t-prod-nombre { font-size: 14px; font-weight: 700; color: var(--navy); line-height: 1.3; margin: 0 0 4px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; min-height: 36px }
.t-prod-presentacion { font-size: 12px; color: var(--muted2); margin-bottom: 10px }
.t-prod-precios { display: flex; align-items: baseline; gap: 8px; margin-bottom: 4px }
.t-prod-precio { font-size: 20px; font-weight: 800; color: var(--navy); letter-spacing: -.02em; line-height: 1.1 }
.t-prod-mercado { font-size: 13px; color: var(--muted2); text-decoration: line-through }
.t-prod-iva { font-size: 10px; color: var(--muted2); font-weight: 600; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 12px }
.t-prod-tier { display: inline-block; font-size: 9px; font-weight: 800; padding: 2px 8px; border-radius: 999px; letter-spacing: .04em; text-transform: uppercase; margin-bottom: 12px }
.t-prod-add { margin: 0 14px 14px }
.t-prod-cant { margin: 0 14px 14px }
/* COMPROMISO LIMPACK — 3 razones */
.t-promise {
  background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
  border: 1.5px solid #86efac; border-radius: 18px;
  padding: 28px 24px; margin-bottom: 24px;
}
.t-promise-head { text-align: center; margin-bottom: 22px }
.t-promise-tag {
  display: inline-block; background: #14532d; color: #fff;
  padding: 4px 14px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: .12em;
  margin-bottom: 10px;
}
.t-promise-head h2 {
  margin: 0; font-size: clamp(20px, 3.5vw, 28px); font-weight: 900;
  color: #14532d; letter-spacing: -.01em;
}
.t-promise-grid {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px;
}
@media (max-width: 980px) { .t-promise-grid { grid-template-columns: repeat(2, 1fr) } }
@media (max-width: 520px) { .t-promise-grid { grid-template-columns: 1fr; gap: 12px } }
.t-promise-item {
  background: #fff; border-radius: 14px; padding: 22px 18px;
  text-align: center; border: 1px solid rgba(20,83,45,.08);
}
.t-promise-icon { font-size: 38px; line-height: 1; margin-bottom: 10px }
.t-promise-item strong {
  display: block; font-size: 16px; color: #14532d; font-weight: 900;
  letter-spacing: -.01em; margin-bottom: 6px;
}
.t-promise-item p {
  color: #475569; font-size: 13px; line-height: 1.5; margin: 0;
}

/* UPSELL HERO — banner gigante para suscribirse */
.t-upsell-hero {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 60%, #00c4d4 100%);
  color: #fff; border-radius: 20px;
  padding: 32px 28px; margin-bottom: 24px;
}
.t-upsell-hero-bg {
  position: absolute; top: -50%; right: -20%; width: 60%; height: 200%;
  background: radial-gradient(circle, rgba(255,196,0,.25), transparent 60%);
  pointer-events: none;
}
.t-upsell-hero-content { position: relative; z-index: 1; max-width: 720px }
.t-upsell-hero-tag {
  display: inline-block; background: rgba(255,255,255,.16);
  border: 1px solid rgba(255,255,255,.25);
  color: #ffe28c; padding: 4px 14px; border-radius: 999px;
  font-size: 10px; font-weight: 800; letter-spacing: .12em;
  margin-bottom: 12px;
}
.t-upsell-hero-h2 {
  margin: 0 0 8px; font-size: clamp(22px, 4vw, 32px); font-weight: 900;
  letter-spacing: -.02em; line-height: 1.15;
}
.t-upsell-hero-sub {
  margin: 0 0 18px; color: rgba(255,255,255,.85); font-size: 15px; line-height: 1.5;
}
.t-upsell-hero-list {
  list-style: none; padding: 0; margin: 0 0 22px;
  display: flex; flex-direction: column; gap: 8px;
}
.t-upsell-hero-list li {
  display: flex; gap: 12px; align-items: flex-start;
  font-size: 14px; line-height: 1.45; color: rgba(255,255,255,.92);
}
.t-upsell-hero-list li span {
  width: 24px; height: 24px; border-radius: 50%;
  background: rgba(0,196,212,.20); color: #7ce8f0;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; flex-shrink: 0;
}
.t-upsell-hero-list li strong { color: #fff }
.t-upsell-hero-cta-row {
  display: flex; gap: 14px; align-items: center; flex-wrap: wrap;
}
.t-upsell-hero-cta {
  background: #ffe28c; color: #1a3461; border: 0;
  padding: 14px 24px; border-radius: 999px;
  font-weight: 900; font-size: 15px; cursor: pointer;
  font-family: inherit; white-space: nowrap;
  box-shadow: 0 8px 24px rgba(255, 226, 140, .35);
  transition: transform .15s, box-shadow .15s;
}
.t-upsell-hero-cta:hover { transform: translateY(-1px); box-shadow: 0 10px 28px rgba(255, 226, 140, .50) }
.t-upsell-hero-fineprint { font-size: 11px; color: rgba(255,255,255,.65) }

.t-prod-precios { display: flex; flex-direction: column; gap: 2px; margin-bottom: 12px; align-items: flex-start }
.t-prod-mercado { font-size: 10px; color: var(--muted2); text-transform: uppercase; letter-spacing: .06em; font-weight: 700; line-height: 1.2 }
.t-prod-mercado-precio { text-decoration: line-through; font-weight: 600; margin-left: 3px }
.t-prod-precio { font-size: 22px; font-weight: 900; color: var(--navy); letter-spacing: -.02em; line-height: 1.1; display: inline-flex; align-items: baseline; gap: 6px }
.t-prod-iva { font-size: 9px; color: var(--muted2); font-weight: 600; letter-spacing: .04em; text-transform: uppercase }
.t-prod-add { background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; padding: 10px 16px; font-weight: 800; font-size: 13px; cursor: pointer; font-family: inherit; transition: transform .12s }
.t-prod-add:hover { transform: translateY(-1px) }
.t-prod-cant { display: flex; align-items: center; justify-content: space-between; gap: 6px; background: #f1f5f9; border-radius: 999px; padding: 4px }
.t-prod-cant button { width: 32px; height: 32px; border-radius: 50%; border: 0; background: var(--navy); color: #fff; font-weight: 800; font-size: 16px; cursor: pointer; font-family: inherit }
.t-prod-cant button:hover { background: var(--teal); color: var(--navy) }
.t-prod-cant span { font-weight: 800; font-size: 14px; color: var(--navy); flex: 1; text-align: center }

/* Carrito flotante */
.t-cart-fab { position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; padding: 14px 24px; font-weight: 800; font-size: 14px; cursor: pointer; font-family: inherit; box-shadow: 0 12px 32px rgba(0,196,212,.45); z-index: 50; max-width: calc(100% - 40px) }
.t-cart-fab:hover { transform: translateX(-50%) translateY(-2px) }

/* Drawer carrito */
.t-drawer-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); backdrop-filter: blur(4px); z-index: 1000; display: flex; justify-content: flex-end }
.t-drawer { width: 100%; max-width: 460px; background: #fff; height: 100%; padding: 24px; box-sizing: border-box; overflow-y: auto; animation: slideIn .25s ease }
@keyframes slideIn { from { transform: translateX(100%) } to { transform: translateX(0) } }
.t-drawer-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px }
.t-drawer-head h2 { font-size: 22px; font-weight: 900; color: var(--navy); margin: 0 }
.t-drawer-x { width: 36px; height: 36px; border-radius: 50%; background: #f1f5f9; border: 0; cursor: pointer; font-size: 16px; color: var(--muted2); font-family: inherit }
.t-drawer-empty { text-align: center; padding: 40px 20px; color: var(--muted2) }
.t-drawer-items { list-style: none; padding: 0; margin: 0 0 20px; display: flex; flex-direction: column; gap: 10px }
.t-drawer-items li { display: grid; grid-template-columns: 1fr auto auto auto; align-items: center; gap: 8px; padding: 10px; background: #f8fafc; border-radius: 10px; font-size: 13px }
.t-drawer-info strong { display: block; color: var(--navy); font-weight: 800 }
.t-drawer-info span { color: var(--muted2); font-size: 11px }
.t-drawer-cant { display: flex; align-items: center; gap: 4px; background: #fff; border: 1px solid var(--line2); border-radius: 999px; padding: 2px }
.t-drawer-cant button { width: 22px; height: 22px; border: 0; border-radius: 50%; background: #f1f5f9; color: var(--navy); cursor: pointer; font-weight: 800; font-family: inherit; line-height: 1 }
.t-drawer-cant span { font-weight: 800; min-width: 20px; text-align: center; font-size: 12px }
.t-drawer-precio { font-weight: 800; color: var(--navy); white-space: nowrap; font-size: 13px }
.t-drawer-quitar { background: none; border: 0; color: #ef4444; cursor: pointer; font-size: 18px; font-weight: 800; padding: 0 4px; font-family: inherit }
.t-drawer-totales { padding: 14px 16px; background: #f8fafc; border-radius: 12px; margin-bottom: 16px }
.t-drawer-totales > div { display: flex; justify-content: space-between; align-items: baseline }
.t-drawer-totales strong { font-size: 22px; font-weight: 900; color: var(--navy) }
.t-drawer-info-text { font-size: 11px; color: var(--muted2); margin: 8px 0 0 }
.t-drawer-actions { display: flex; gap: 10px; justify-content: flex-end }
.t-btn-ghost { padding: 12px 20px; background: #f1f5f9; color: var(--navy); border: 0; border-radius: 999px; font-weight: 700; font-size: 13px; cursor: pointer; font-family: inherit }
.t-btn-primary { padding: 12px 22px; background: linear-gradient(135deg, var(--blue), var(--teal)); color: #fff; border: 0; border-radius: 999px; font-weight: 800; font-size: 13px; cursor: pointer; font-family: inherit; box-shadow: 0 6px 18px rgba(0,196,212,.3); text-decoration: none; display: inline-flex; align-items: center; gap: 6px }

/* Success */
.t-success { flex: 1; display: flex; align-items: center; justify-content: center; padding: 60px 20px }
.t-success-card { background: #fff; max-width: 460px; padding: 40px; border-radius: 24px; text-align: center; box-shadow: 0 20px 60px rgba(26,52,97,.15) }
.t-success-icon { width: 70px; height: 70px; border-radius: 50%; background: linear-gradient(135deg, #22c55e, #10b981); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 38px; font-weight: 900; margin: 0 auto 20px }
.t-success-card h1 { font-size: 28px; font-weight: 900; color: var(--navy); margin: 0 0 12px; letter-spacing: -.01em }
.t-success-card p { font-size: 15px; color: #334155; margin: 8px 0 }
.t-success-msg { color: var(--muted2); font-size: 14px; line-height: 1.5; margin: 16px 0 24px !important }
.t-success-actions { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap }
`
